import React, { useId } from 'react';
import { RiInformationLine } from 'react-icons/ri';
import { MacroFoodIcon } from '../../CreateDietPlanV2/components/MacroFoodIcon';
import { MACRO_ICON } from '../copy';
import { SCORE_BANDS, type ScoreBand } from '../lib/sufficiency';
import type { Macro } from '../types';

/** "i" button with a hover/focus bubble. `align` picks the side the bubble opens towards. */
export function InfoTip({
  label,
  title,
  align = 'left',
  wide,
  children,
}: {
  label: string;
  title: string;
  align?: 'left' | 'right';
  wide?: boolean;
  children: React.ReactNode;
}) {
  const id = useId();
  return (
    <span className="ns-tt">
      <button type="button" className="ns-info" aria-label={`About ${label}`} aria-describedby={id}>
        <RiInformationLine aria-hidden />
      </button>
      <span className={`ns-tt-b ns-tt-${align}${wide ? ' ns-tt-w' : ''}`} role="tooltip" id={id}>
        <b>{title}</b>
        {children}
      </span>
    </span>
  );
}

export function HelpBody({ intro, points }: { intro: string; points: string[] }) {
  return (
    <>
      {intro}
      <ul>
        {points.map((p) => (
          <li key={p}>{p}</li>
        ))}
      </ul>
    </>
  );
}

/** Score-band pills in a fixed left column with descriptions beside them (Average score tooltip). */
export function BandLegend({ intro }: { intro: string }) {
  return (
    <>
      {intro}
      <ul className="ns-tags">
        {SCORE_BANDS.map((b) => (
          <li key={b.key}>
            <BandPill band={b} />
            <span className="ns-tags-d">{b.description}</span>
          </li>
        ))}
      </ul>
    </>
  );
}

export function BandPill({ band }: { band: ScoreBand }) {
  return (
    <span className={`ns-pill ns-pill-${band.key}`}>
      <span aria-hidden>{band.emoji}</span> {band.label}
    </span>
  );
}

/** Coloured square holding a macro glyph, as in the legend and the Macros panel. */
export function MacroMark({ macro, size = 'sm' }: { macro: Macro; size?: 'sm' | 'lg' }) {
  return (
    <span className={`ns-mt ns-mt-${size} ns-mt-${macro}`} aria-hidden>
      <MacroFoodIcon k={MACRO_ICON[macro]} size={size === 'lg' ? 16 : 13} />
    </span>
  );
}

export function Meter({ pct }: { pct: number }) {
  return (
    <div className="ns-meter" aria-hidden>
      <i style={{ width: `${pct}%` }} />
    </div>
  );
}
